import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toObject } from '../../data-utils';
import { useSelector } from '../../hook-utils';
import { Wrapup } from '../../model';
import { actions } from '../../store';
import { selectConfigProp } from '../../store/reducers/config';
import { selectMedia, selectMediaWrapupCodes } from '../../store/reducers/media';
import { selectWrapups, selectWrapupsPath, shouldFetchWrapupItems } from '../../store/reducers/wrapups';
import { ReactComponent as CheckIcon } from '../icons/menu-check.svg';
import { TreeNode, TreeView, TreeViewButton } from '../TreeView';
import './WrapupsPage.scss';

interface Props {
  mediaId: string
}

interface ContextData {
  checked: Readonly<Record<string, true>>
  path?: string[]
  onCheck(id: string): void
}

const Context = React.createContext<ContextData | null>(null)

const root: Wrapup = {
  id: 'SYS',
  name: 'System',
  folder: true,
}

type Checked = Record<string, true>

export default function WrapupsPage({ mediaId }: Props) {
  const media = useSelector(s => selectMedia(s, mediaId))
  const singleSelect = useSelector(s => selectConfigProp(s, 'wrapupSingleSelect', false))
  const path = useSelector(s => media && selectWrapupsPath(s, media.wrapupFolder))
  const wrapups = useSelector(s => selectMediaWrapupCodes(s, mediaId))
  const dispatch = useDispatch()
  const wrapupFolder = media && media.wrapupFolder
  const checked = useMemo<Checked>(() => toObject(wrapups, w => w, () => true as true), [wrapups])
  const setChecked = useCallback((value: Checked) => {
    dispatch(actions.setWrapupCodes(mediaId, Object.keys(value)))
  }, [dispatch, mediaId])

  const onCheck = useCallback((id: string) => {
    if (id in checked) {
      if (singleSelect) {
        setChecked({})
      } else {
        const { [id]: _, ...rest } = checked
        setChecked(rest)
      }
    } else {
      if (singleSelect) {
        setChecked({ [id]: true })
      } else {
        setChecked({ ...checked, [id]: true })
      }
    }
  }, [checked, setChecked, singleSelect])

  useEffect(() => {
    if (!wrapupFolder) return

    dispatch(actions.fetchWrapupsDeep.request(wrapupFolder))
  }, [dispatch, wrapupFolder])

  const context = useMemo<ContextData>(() => ({
    checked,
    path,
    onCheck,
  }), [checked, onCheck, path])

  return <div className='WrapupsPage'>
    <h2>Wrapups</h2>
    <Context.Provider value={context}>
      <TreeView>
        <WrapupNode initial wrapup={root} />
      </TreeView>
    </Context.Provider>
  </div>
}

interface WrapupNodeProps {
  initial?: boolean
  wrapup: Wrapup
}

function WrapupNode({ initial, wrapup }: WrapupNodeProps) {
  const ctx = useContext(Context)
  const items = useSelector(s => wrapup.folder ? selectWrapups(s, wrapup.id) : null)
  const shouldFetch = useSelector(s => wrapup.folder && shouldFetchWrapupItems(s, wrapup.id))
  const dispatch = useDispatch()

  useEffect(() => {
    if (shouldFetch) {
      dispatch(actions.fetchWrapupFolder.request(wrapup.id))
    }
  }, [dispatch, shouldFetch, wrapup.id])

  if (!ctx) { return null }
  const { checked, onCheck } = ctx

  const getNodes = items === null ? undefined : () => {
    if (!items) {
      return <TreeNode>loading...</TreeNode>
    } else if (items.length === 0) {
      return <TreeNode>no items</TreeNode>
    } else {
      return items.map(w => <WrapupNode key={w.id + w.name} wrapup={w} />)
    }
  }

  return <TreeNode getNodes={getNodes} initialExpanded={initial || (ctx.path && ctx.path.includes(wrapup.id))} noButton={initial}>
    {!getNodes && <TreeViewButton onClick={() => onCheck(wrapup.id)}>
      {wrapup.id in checked && <CheckIcon />}
    </TreeViewButton>}
    {wrapup.name}
  </TreeNode>
}
